import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { BadgeCheck } from "lucide-react";
import { toast } from "sonner";
import { useApp } from "@/state/AppContext";
import { api, apiErrorMessage, rows } from "@/lib/api";
import { StarRating } from "@/components/StarRating";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

/** The "reviews" tab's whole flow (list + write-review form), made self-contained the way every
 * `components/business/*Section.jsx` already is — it used to fetch through `BusinessMiniSite`'s
 * own `loadReviews`/`submitReview` and `reviews` state, which nothing else on the page read, so
 * moving the fetch and the form state in here removes that state from the page container
 * entirely rather than threading it through `Storefront` as props. Not in the PR3 brief's
 * component list (the design floor doesn't name a "reviews" shell piece either), but it's a real
 * flow on the current page and parity requires keeping it — always present, like the old
 * "reviews" tab was. */
export function ReviewsSection({ business }) {
  const { t, auth, tenant } = useApp();
  const navigate = useNavigate();
  const [reviews, setReviews] = useState([]);
  // R5-11: the rating summary lives on the business row the page loaded once; after a withdraw the
  // list AND this summary must be re-read, or the public page keeps counting a review that is gone.
  const [stats, setStats] = useState(null);
  const [rating, setRating] = useState(5);
  const [body, setBody] = useState("");
  const [submitting, setSubmitting] = useState(false);
  // RF-9: the viewer's own review of THIS shop, in any status. A second one is refused server-side
  // (unique constraint), so a fresh form for someone who already wrote one is a dead end.
  const [mine, setMine] = useState(null);
  const [withdrawing, setWithdrawing] = useState(false);
  // UAT P4x: an owner of THIS business must not see the write-review form on their own page.
  // `role_profiles` (from /auth/me) carries `business_id` for each owner seat.
  // R3-7: that alone never matched in practice — `ensure_role_profile` grants ONE city-wide
  // `business_owner` seat with `business_id` NULL (membership lives in BusinessMember), so an owner
  // still saw "Write a review" on their own shop. Ask the owner desk which shops this person runs.
  // While that answer is pending the form stays hidden (no flash of a form they must not have).
  const hasOwnerSeat = (auth?.role_profiles || []).some((p) => p.role_type === "business_owner");
  const [ownedIds, setOwnedIds] = useState(null);
  useEffect(() => {
    if (!hasOwnerSeat) return;
    let live = true;
    api
      .get("/owner/businesses")
      .then(({ data }) => live && setOwnedIds(rows(data).map((b) => b.id)))
      .catch(() => live && setOwnedIds([]));
    return () => {
      live = false;
    };
  }, [hasOwnerSeat]);
  const ownsThisBusiness =
    (auth?.role_profiles || []).some(
      (p) => p.role_type === "business_owner" && p.business_id && p.business_id === business.id,
    ) ||
    (hasOwnerSeat && (ownedIds === null || ownedIds.includes(business.id)));

  const loadReviews = useCallback(async () => {
    const { data } = await api.get("/reviews", {
      params: { reviewable_type: "business", reviewable_id: business.id },
    });
    setReviews(rows(data));
  }, [business.id]);

  useEffect(() => {
    loadReviews();
  }, [loadReviews]);

  useEffect(() => {
    if (!auth) return;
    api
      .get("/reviews/mine")
      .then(({ data }) => setMine(rows(data).find((r) => r.reviewable_id === business.id) || null))
      .catch(() => setMine(null));
  }, [auth, business.id]);

  const withdrawReview = async () => {
    try {
      await api.delete(`/reviews/${mine.id}`);
      toast.success(t("account.reviewWithdrawn"));
      // R4-8: a withdrawn review is its own state; writing again reopens the SAME row server-side.
      setMine({ ...mine, status: "withdrawn" });
      setWithdrawing(false);
      await loadReviews().catch(() => undefined);
      if (tenant && business.slug) {
        try {
          const { data } = await api.get(`/directory/businesses/by-slug/${business.slug}`, { params: { tenant_id: tenant.id } });
          setStats({ rating_avg: data.rating_avg, rating_count: data.rating_count });
        } catch {
          // The list is already fresh; a failed summary re-read must not undo a successful withdraw.
        }
      }
    } catch (e) {
      toast.error(apiErrorMessage(e, t("errors.generic")));
    }
  };

  const submitReview = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/reviews", {
        reviewable_type: "business",
        reviewable_id: business.id,
        rating,
        body,
      });
      setMine(data);
      setBody("");
      toast.success(t("business.reviewPending"));
    } catch (e) {
      toast.error(apiErrorMessage(e, t("errors.generic")));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="storefront-section-reviews">
      {/* Storefront S1, B4: a heading + "★ 4.6 · 210 reviews" summary, matching every other
          content block on the page (Menu/About/Plans each have an <h2>) and the handoff's
          "Reviews" / "★ 4.6 · 210 reviews" row. Summary only when there ARE reviews — never a
          fabricated "0 reviews" line. */}
      <div className="flex items-baseline justify-between gap-3">
        <h2 className="font-heading text-xl font-semibold">{t("business.tabReviews")}</h2>
        {(stats ?? business).rating_count > 0 && (
          <span className="text-sm text-muted-foreground" data-testid="reviews-summary">
            {t("storefront.reviewsSummary", {
              rating: Number((stats ?? business).rating_avg).toFixed(1),
              count: (stats ?? business).rating_count,
            })}
          </span>
        )}
      </div>
      <div className="space-y-4" data-testid="review-list">
        {reviews.length === 0 && <p className="text-muted-foreground">{t("business.noReviews")}</p>}
        {reviews.map((r) => (
          <article key={r.id} className="rounded-2xl border border-border p-5" data-testid={`review-${r.id}`}>
            <div className="flex items-center justify-between">
              <p className="font-medium">{r.author_name}</p>
              <StarRating value={r.rating} />
            </div>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{r.body}</p>
            {r.is_verified_transaction && (
              <Badge variant="outline" className="mt-3 gap-1 text-[hsl(var(--primary))]">
                <BadgeCheck size={12} /> {t("common.verified")}
              </Badge>
            )}
            {r.reply && (
              <div className="mt-4 rounded-xl bg-muted/60 p-4">
                <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                  {t("business.ownerReply")}
                </p>
                <p className="mt-1 text-sm">{r.reply.body}</p>
              </div>
            )}
          </article>
        ))}
      </div>

      {ownsThisBusiness ? null : auth && mine && mine.status !== "withdrawn" ? (
        <div className="rounded-2xl border border-border p-6" data-testid="review-mine">
          <h3 className="font-heading text-lg font-semibold">{t("business.yourReview")}</h3>
          <div className="mt-2 flex items-center gap-3">
            <StarRating value={mine.rating} />
            <Badge variant="outline">{t(`common.${mine.status}`)}</Badge>
          </div>
          {mine.body && <p className="mt-2 text-sm text-muted-foreground">{mine.body}</p>}
          {(mine.status === "pending" || mine.status === "approved") && (
            <div className="mt-3 flex flex-wrap items-center gap-2 text-sm">
              <span className="text-xs text-muted-foreground">
                {mine.status === "pending" ? t("account.reviewPendingNote") : t("account.reviewPublishedNote")}
              </span>
              {withdrawing ? (
                <>
                  <Button size="sm" className="rounded-full" data-testid="review-withdraw-confirm" onClick={withdrawReview}>
                    {t("account.withdrawReview")}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setWithdrawing(false)}>
                    {t("common.cancel")}
                  </Button>
                </>
              ) : (
                <Button size="sm" variant="outline" className="rounded-full" data-testid="review-withdraw" onClick={() => setWithdrawing(true)}>
                  {t("account.withdrawReview")}
                </Button>
              )}
            </div>
          )}
          {mine.status === "rejected" && (
            <p className="mt-3 text-xs text-muted-foreground" data-testid="review-mine-rejected">
              {t("business.reviewNotPublished")}
            </p>
          )}
        </div>
      ) : auth ? (
        <div className="rounded-2xl border border-border p-6" data-testid="review-form">
          <h3 className="font-heading text-lg font-semibold">{t("business.writeReview")}</h3>
          <p className="mt-3 text-xs font-semibold uppercase tracking-widest text-muted-foreground">
            {t("business.rating")}
          </p>
          <div className="mt-2">
            <StarRating value={rating} size={22} onChange={setRating} testId="review-rating" />
          </div>
          <Textarea
            data-testid="review-body-input"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder={t("business.reviewBodyPlaceholder")}
            className="mt-4"
            rows={4}
          />
          <Button className="mt-4 rounded-full" data-testid="review-submit-btn" disabled={submitting} onClick={submitReview}>
            {submitting ? t("common.saving") : t("common.submit")}
          </Button>
        </div>
      ) : (
        <Button variant="outline" className="rounded-full" data-testid="review-signin-btn" onClick={() => navigate("/login")}>
          {t("business.signInToReview")}
        </Button>
      )}
    </div>
  );
}
